import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { concat, debounceTime, filter, map, of, switchMap } from 'rxjs';

import { EffectsBase } from '@ps/core-data-access';
import { DEFAULT_COUNTRY_CODE } from '@ps/survey-data-access';
import { EPC_ESTIMATE_DEBOUNCE_MS } from '@ps/survey-utils';

import { SurveyEpcService } from '../../services/survey-epc/survey-epc.service';
import {
  SurveyCreationAudienceActions,
  SurveyCreationAudienceApiActions,
} from '../audience/survey-creation-audience.actions';
import { SurveyCreationCoreApiActions } from '../core/survey-creation-core.actions';
import {
  selectMarginForm,
  selectMetadataForm,
} from '../audience/survey-creation-audience.selectors';
import { SurveyCreationInsightsApiActions } from './survey-creation-insights.actions';
import {
  selectEpcPlatformByCountry,
  selectEpcRequestedQuery,
} from './survey-creation-insights.selectors';
import {
  epcEstimateQuery,
  epcPlatformCountryCode,
  isEpcEstimateRequestable,
  isSameEpcEstimateQuery,
} from './utils/survey-epc.helper';

@Injectable()
export class SurveyCreationEpcEffects extends EffectsBase {
  private readonly _actions$ = inject(Actions);
  private readonly _store = inject(Store);
  private readonly _service = inject(SurveyEpcService);

  public readonly loadEpcEstimate$ = createEffect(() =>
    this._actions$.pipe(
      ofType(
        SurveyCreationAudienceActions.updateMetadata,
        SurveyCreationAudienceActions.settleBuyerCpi,
        SurveyCreationCoreApiActions.loadActiveSurveyForEditSuccess,
      ),
      debounceTime(EPC_ESTIMATE_DEBOUNCE_MS),
      concatLatestFrom(() => [
        this._store.select(selectMetadataForm),
        this._store.select(selectMarginForm),
        this._store.select(selectEpcRequestedQuery),
      ]),
      map(([, metadata, margin, requestedQuery]) => ({
        query: epcEstimateQuery(metadata, margin),
        requestedQuery,
      })),
      filter(
        ({ query, requestedQuery }) =>
          !requestedQuery || !isSameEpcEstimateQuery(requestedQuery, query),
      ),
      switchMap(({ query }) =>
        !isEpcEstimateRequestable(query)
          ? of(SurveyCreationInsightsApiActions.clearEpcEstimate())
          : concat(
              of(
                SurveyCreationInsightsApiActions.loadEpcEstimatePending({
                  query,
                }),
              ),
              this._service.estimate(query).pipe(
                map((estimate) =>
                  SurveyCreationInsightsApiActions.loadEpcEstimateSuccess({
                    estimate,
                  }),
                ),
                this.psCatchError((error) =>
                  of(
                    SurveyCreationInsightsApiActions.loadEpcEstimateFailure({
                      error: error.message,
                    }),
                  ),
                ),
              ),
            ),
      ),
    ),
  );

  /**
   * Platform — fetched once per country. Presence in `platformByCountry` is the
   * cache, so switching back to a country already resolved costs nothing.
   */
  public readonly loadEpcPlatform$ = createEffect(() =>
    this._actions$.pipe(
      ofType(
        SurveyCreationAudienceActions.updateMetadata,
        SurveyCreationCoreApiActions.loadActiveSurveyForEditSuccess,
        SurveyCreationAudienceApiActions.createSurveyFromTemplateSuccess,
      ),
      concatLatestFrom(() => this._store.select(selectMetadataForm)),
      map(([, metadata]) =>
        epcPlatformCountryCode(metadata, DEFAULT_COUNTRY_CODE),
      ),
      concatLatestFrom(() => this._store.select(selectEpcPlatformByCountry)),
      filter(
        ([countryCode, platformByCountry]) =>
          !(countryCode in platformByCountry),
      ),
      switchMap(([countryCode]) =>
        this._service.platform(countryCode).pipe(
          map((platform) =>
            SurveyCreationInsightsApiActions.loadEpcPlatformSuccess({
              countryCode,
              platform,
            }),
          ),
          this.psCatchError((error) =>
            of(
              SurveyCreationInsightsApiActions.loadEpcPlatformFailure({
                error: error.message,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  /**
   * Actual — only meaningful once the survey is created, so it is keyed off
   * the edit-load action. That action IS the mode discriminator, hence no
   * surveyId guard.
   */
  public readonly loadEpcActual$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationCoreApiActions.loadActiveSurveyForEditSuccess),
      switchMap(({ survey }) =>
        this._service.actual(survey.id).pipe(
          map((actual) =>
            SurveyCreationInsightsApiActions.loadEpcActualSuccess({ actual }),
          ),
          this.psCatchError((error) =>
            of(
              SurveyCreationInsightsApiActions.loadEpcActualFailure({
                error: error.message,
              }),
            ),
          ),
        ),
      ),
    ),
  );
}
